import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { VariantQtyRows } from './VariantQtyRows';
import { ShippingAddressForm } from './ShippingAddressForm';
import { PaymentStatusToggle } from './PaymentStatusToggle';
import { get, post, ApiError } from '@/lib/api';
import { useSession } from '@/lib/auth';
import {
  type AnyInventoryItem, type ShippingFields, type ShopifyVariant, type OrderLine,
  EMPTY_SHIPPING, shippingComplete, buildSampleOrderPayload,
} from '@/lib/ordering';

type VariantsPayload = {
  variants?: ShopifyVariant[];
  product?: { title?: string; image?: string };
  error?: string;
};

// Sample/blank order modal — port of admin-dashboard's openSampleOrderModal /
// submitSampleOrder. Creates a REAL Shopify order via create-order and logs
// it to sampleOrders; confirm()-gated.
export function SampleOrderDialog({
  item,
  open,
  onClose,
  onPlaced,
}: {
  item: AnyInventoryItem | null;
  open: boolean;
  onClose: () => void;
  onPlaced: () => void;
}) {
  const { user } = useSession();
  const [qty, setQty] = useState<Record<string, number>>({});
  const [shipping, setShipping] = useState<ShippingFields>(EMPTY_SHIPPING);
  const [email, setEmail] = useState('');
  const [financialStatus, setFinancialStatus] = useState<'paid' | 'pending'>('paid');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setQty({});
      setShipping(EMPTY_SHIPPING);
      setEmail('');
      setFinancialStatus('paid');
      setNote('');
    }
  }, [open, item?.id]);

  const { data, isLoading } = useSWR<VariantsPayload>(
    open && item?.shopifyProductId ? `/api/shopify-product?productId=${encodeURIComponent(item.shopifyProductId)}` : null, get
  );

  const lines = useMemo<OrderLine[]>(
    () => (data?.variants || [])
      .map((v) => ({ variant_id: v.id, quantity: qty[String(v.id)] || 0 }))
      .filter((l) => l.quantity > 0),
    [data, qty]
  );
  const totalUnits = lines.reduce((s, l) => s + l.quantity, 0);

  if (!item) return null;
  const productName = [item.brand, item.color, item.category].filter(Boolean).join(' ');

  const submit = async () => {
    if (!lines.length) { toast.error('Enter at least one item quantity'); return; }
    if (!shippingComplete(shipping)) { toast.error('Fill in all required shipping fields'); return; }
    if (!confirm(`Create a REAL sample order for ${totalUnits} units of "${productName}"? This creates an actual Shopify order.`)) return;

    setBusy(true);
    try {
      const payload = buildSampleOrderPayload({
        item, lines, shipping, email, financialStatus, note,
        orderedBy: user?.email || 'console', totalUnits,
      });
      const r = await post<{ success?: boolean; orderNumber?: string; order?: { name?: string } }>(
        '/api/sample-orders', payload
      );
      toast.success(`Sample order ${r.orderNumber || r.order?.name || ''} created — ${totalUnits} units of ${productName}`);
      onPlaced();
      onClose();
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
      toast.error(body?.message || body?.error || (e as Error).message || 'Failed to create sample order');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{productName}</DialogTitle>
          <DialogDescription>
            Style {item.styleNumber || '-'} · Color {item.colorCode || '-'} · Shopify {item.shopifyProductId || 'N/A'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
          <div className="rounded-md border border-border bg-bg/40 p-3">
            {data?.product?.image && (
              <img src={data.product.image} alt={productName} className="mb-2 h-32 w-full rounded-sm bg-bg object-contain" />
            )}
            {!item.shopifyProductId ? (
              <div className="p-4 text-center text-[12px] text-muted">No Shopify product linked</div>
            ) : isLoading ? (
              <div className="space-y-1.5 p-2">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
              </div>
            ) : data?.error ? (
              <div className="p-3 text-center text-[11.5px] text-danger">{String(data.error)}</div>
            ) : (
              <VariantQtyRows variants={data?.variants || []} qty={qty} onChange={(id, n) => setQty((q) => ({ ...q, [id]: n }))} />
            )}
            <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-[11.5px]">
              <span className="text-muted">Total units</span>
              <span className="font-semibold">{totalUnits} units</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-md border border-border bg-bg/40 p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-subtle">Ship to</div>
              <ShippingAddressForm value={shipping} onChange={setShipping} email={email} onEmailChange={setEmail} emailLabel="Email (optional)" />
            </div>
            <div className="rounded-md border border-border bg-bg/40 p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-subtle">Payment status</div>
              <PaymentStatusToggle value={financialStatus} onChange={setFinancialStatus} />
            </div>
            <div className="rounded-md border border-border bg-bg/40 p-3">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-subtle">Notes</div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Sample for Daren, size reference check…"
                className="h-16 w-full resize-y rounded-sm border border-border bg-bg px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <Button className="w-full" size="lg" disabled={busy || totalUnits === 0} onClick={submit}>
              {busy ? 'Creating order…' : 'Create Sample Order'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
